import { useEffect } from "react";

/**
 * Sets document.title, meta description/keywords, canonical + OpenGraph tags.
 * Reversible: restores original title on unmount and removes tags it added.
 * Google fully renders JS since 2019, so client-side meta injection is picked up.
 */
export default function Seo({ title, description, keywords, image, url, type = "website" }) {
  useEffect(() => {
    const created = [];

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", value);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("href", href);
    };

    const prevTitle = document.title;
    if (title) document.title = title;

    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);

    // OpenGraph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", url || (typeof window !== "undefined" ? window.location.href : ""));

    // Twitter card
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    setLink("canonical", url || (typeof window !== "undefined" ? window.location.href : ""));

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [title, description, keywords, image, url, type]);

  return null;
}
